'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, LogIn, Mail } from 'lucide-react';
import type { AuthRole } from './AuthScreen';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  role: AuthRole;
}

const ROLE_ROUTES: Record<string, string> = {
  admin: '/admin-panel',
  staff: '/admin-panel',
  student: '/student-dashboard',
};

const SITE_URL =
  process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/$/, '') ||
  'https://yashcomput4067.builtwithrocket.new';

export default function LoginForm({ role }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  const router = useRouter();
  const { signIn } = useAuth();

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const result = await signIn(data.email, data.password);

      // Enforce email verification
      const emailVerified = result?.user?.email_confirmed_at;
      if (!emailVerified) {
        setIsLoading(false);
        toast.error('Please verify your email before logging in. Check your inbox for the verification link.');
        return;
      }

      const userRole = result?.user?.user_metadata?.role || 'student';
      const route = ROLE_ROUTES[userRole] || '/student-dashboard';
      toast.success(`Welcome back! Redirecting to ${userRole} portal...`);
      setTimeout(() => router.push(route), 800);
    } catch (error: any) {
      setIsLoading(false);
      if (error?.message?.includes('Invalid login credentials')) {
        toast.error('Invalid credentials — check your email and password');
      } else if (error?.message?.includes('Email not confirmed')) {
        toast.error('Please verify your email address before logging in.');
      } else {
        toast.error(error?.message || 'Sign in failed. Please try again.');
      }
    }
  };

  const handleForgotPassword = async () => {
    const emailToReset = forgotEmail.trim() || getValues('email').trim();
    if (!emailToReset || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToReset)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSendingReset(true);
    try {
      const supabase = createClient();

      // Check if email belongs to a registered user in user_profiles
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('id, email')
        .eq('email', emailToReset)
        .maybeSingle();

      if (profileError) {
        // If RLS blocks the query, fall through to attempt reset anyway
        console.warn('Profile lookup error:', profileError.message);
      }

      // If no profile found, show invalid email error
      if (!profileError && profileData === null) {
        toast.error('Invalid email — no account found with this email address');
        setSendingReset(false);
        return;
      }

      // Send reset email only for registered users
      const { error } = await supabase.auth.resetPasswordForEmail(emailToReset, {
        redirectTo: `${SITE_URL}/auth/callback?type=recovery`,
      });

      if (error) {
        toast.error('Failed to send reset email: ' + error.message);
      } else {
        toast.success(`Password reset email sent to ${emailToReset} — check your inbox`);
        setShowForgot(false);
        setForgotEmail('');
      }
    } catch (err: any) {
      toast.error('Error sending reset email: ' + (err?.message || 'Unknown error'));
    } finally {
      setSendingReset(false);
    }
  };

  if (showForgot) {
    return (
      <div className="space-y-4">
        <div className="text-center mb-2">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-3"
            style={{ background: 'rgba(108,99,255,0.15)', border: '1px solid rgba(108,99,255,0.3)' }}>
            <Mail size={22} style={{ color: 'var(--primary)' }} />
          </div>
          <h3 className="font-bold text-foreground text-lg">Reset Password</h3>
          <p className="text-xs text-muted-foreground mt-1">Enter your registered email to receive a password reset link</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Email Address</label>
          <input
            type="email"
            value={forgotEmail}
            onChange={e => setForgotEmail(e.target.value)}
            placeholder="your@email.com"
            className="w-full px-4 py-2.5 rounded-xl text-sm text-foreground placeholder-muted-foreground transition-all duration-200 outline-none focus:ring-2 focus:ring-primary"
            style={{ background: 'var(--input)', border: '1px solid var(--border)' }}
          />
        </div>
        <button
          onClick={handleForgotPassword}
          disabled={sendingReset}
          className="w-full py-3 rounded-xl font-semibold text-sm btn-primary flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {sendingReset ? <Loader2 size={18} className="animate-spin" /> : <Mail size={16} />}
          {sendingReset ? 'Checking & Sending...' : 'Send Reset Link'}
        </button>
        <button
          onClick={() => { setShowForgot(false); setForgotEmail(''); }}
          className="w-full py-2 rounded-xl text-xs font-medium text-muted-foreground hover:text-foreground transition-colors border border-border hover:border-primary"
        >
          ← Back to Login
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">
          Email Address
        </label>
        <input
          type="email"
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
          })}
          placeholder={`${role}@yashcomputers.com`}
          className="w-full px-4 py-2.5 rounded-xl text-sm text-foreground placeholder-muted-foreground transition-all duration-200 outline-none focus:ring-2 focus:ring-primary"
          style={{ background: 'var(--input)', border: '1px solid var(--border)' }}
        />
        {errors.email && (
          <p className="mt-1 text-xs text-danger">{errors.email.message}</p>
        )}
      </div>

      {/* Password */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">
          Password
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 6, message: 'Password must be at least 6 characters' },
            })}
            placeholder="Enter your password"
            className="w-full px-4 py-2.5 pr-11 rounded-xl text-sm text-foreground placeholder-muted-foreground transition-all duration-200 outline-none focus:ring-2 focus:ring-primary"
            style={{ background: 'var(--input)', border: '1px solid var(--border)' }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && (
          <p className="mt-1 text-xs text-danger">{errors.password.message}</p>
        )}
      </div>

      {/* Remember Me + Forgot */}
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            {...register('rememberMe')}
            className="w-4 h-4 rounded accent-primary"
          />
          <span className="text-sm text-muted-foreground">Remember me</span>
        </label>
        {role === 'admin' && (
          <button
            type="button"
            onClick={() => { setForgotEmail(getValues('email')); setShowForgot(true); }}
            className="text-sm text-primary hover:text-accent transition-colors"
          >
            Forgot password?
          </button>
        )}
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-3 rounded-xl font-semibold text-sm btn-primary flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ minHeight: '44px' }}
      >
        {isLoading ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <>
            <LogIn size={16} />
            Sign In as {role.charAt(0).toUpperCase() + role.slice(1)}
          </>
        )}
      </button>
    </form>
  );
}